/* ── Sticker data utilities & constants ───────────────────────────────── */

export const TOTAL = 1000;
// VISIBLE_TOTAL = TOTAL - hidden CC slots (995..1000). Use this for progress
// metrics and the "X / Y" display so the user sees the real visible count.
export const VISIBLE_TOTAL = 994;

// Pad legacy 980-byte buffers with 20 zeros at the tail so existing albums
// keep working until the next save bumps them to 1000 bytes server-side.
function decodeAlbumBytes(base64) {
  if (!base64) return new Uint8Array(TOTAL);
  const raw = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
  if (raw.length === TOTAL) return raw;
  const padded = new Uint8Array(TOTAL);
  padded.set(raw.subarray(0, Math.min(raw.length, TOTAL)));
  return padded;
}

// Avatar color palette — deterministic from userId
const AVATAR_COLORS = [
  '#10B981', '#6366F1', '#F59E0B', '#EC4899',
  '#3B82F6', '#8B5CF6', '#EF4444', '#14B8A6',
  '#F97316', '#06B6D4', '#84CC16', '#A855F7',
];

export function userColor(userId) {
  return AVATAR_COLORS[userId % AVATAR_COLORS.length];
}

export function userInitial(name) {
  return (name || '?').charAt(0).toUpperCase();
}

/**
 * Convert a base64-encoded album into the sticker array format the grid
 * components expect: [{ n, owned, reps }, ...]
 *
 * 1000-byte layout: FWC(1-20) · groups A-L(21-980) · CC(981-994) · hidden(995-1000).
 * Mapeo display: slots 1..19 = FWC1..FWC19, slot 20 = FWC00 (portada). El print
 * trae FWC0..FWC19 pero el album guarda los datos 1-indexed; aprovechamos que
 * nadie tenía FWC20 (no existe) para reusar slot 20 como FWC00 sin migración.
 * Legacy 980-byte payloads are padded transparently.
 */
export function base64ToStickers(base64) {
  const bytes = decodeAlbumBytes(base64);
  const stickers = [];
  for (let i = 0; i < TOTAL; i++) {
    const count = bytes[i] || 0;
    stickers.push({
      n: i + 1,
      owned: count > 0,
      reps: count >= 2 ? count : 0,
    });
  }
  return stickers;
}

/**
 * Convert the sticker array back to a base64 string (for saving to backend).
 */
export function stickersToBase64(stickers) {
  const bytes = new Uint8Array(TOTAL);
  for (const s of stickers) {
    if (s.owned) {
      bytes[s.n - 1] = s.reps >= 2 ? s.reps : 1;
    }
  }
  return btoa(String.fromCharCode(...bytes));
}

/**
 * Compute owned count from a base64 album without fully decoding.
 * Skips hidden CC slots (995-1000) so the progress bar matches the visible UI.
 */
export function ownedCountFromBase64(base64) {
  if (!base64) return 0;
  const bytes = decodeAlbumBytes(base64);
  let count = 0;
  for (let i = 0; i < bytes.length; i++) {
    if (bytes[i] > 0 && !isHiddenStickerIdx(i)) count++;
  }
  return count;
}

/**
 * Decode a base64 album into a Uint8Array of 1000 counts.
 * Index i = sticker (i+1), value = count (0-255). Legacy 980-byte payloads
 * are padded with 20 zeros so callers always see a uniform-length buffer.
 */
export function countsFromBase64(base64) {
  return decodeAlbumBytes(base64);
}

/**
 * Build an owned Set from a base64 album. Excludes hidden CC slots (995-1000).
 */
export function ownedSetFromBase64(base64) {
  const set = new Set();
  if (!base64) return set;
  const bytes = decodeAlbumBytes(base64);
  for (let i = 0; i < bytes.length; i++) {
    if (bytes[i] > 0 && !isHiddenStickerIdx(i)) set.add(i + 1);
  }
  return set;
}

/* ── Album sections (World Cup 2026 structure) ─────────────────────────
 *
 * Layout (1-indexed sticker numbers):
 *   1 .. 19    FWC1..FWC19         (FIFA World Cup 2026 master cards)
 *   20         FWC00                (portada — alias del slot 20)
 *   21 .. 980  country sticker pages, 4 countries × 20 stickers per group
 *   981 ..994  CC1..CC14            (Coca-Cola section)
 *   995 ..1000 hidden (legacy slots, no longer used)
 */
export const ALBUM_SECTIONS = [
  { id: 'FWC', label: 'FWC',     subtitle: 'Futbol World Cup',                       from: 1,   to: 20, codePrefix: 'FWC' },
  { id: 'A',   label: 'Grupo A', subtitle: 'México · Sudáfrica · Corea del Sur · República Checa',     from: 21,  to: 100, countries: ['MEX', 'RSA', 'KOR', 'CZE'] },
  { id: 'B',   label: 'Grupo B', subtitle: 'Canadá · Bosnia y Herzegovina · Catar · Suiza',            from: 101, to: 180, countries: ['CAN', 'BIH', 'QAT', 'SUI'] },
  { id: 'C',   label: 'Grupo C', subtitle: 'Brasil · Marruecos · Haití · Escocia',                     from: 181, to: 260, countries: ['BRA', 'MAR', 'HAI', 'SCO'] },
  { id: 'D',   label: 'Grupo D', subtitle: 'USA · Paraguay · Australia · Turquía',                     from: 261, to: 340, countries: ['USA', 'PAR', 'AUS', 'TUR'] },
  { id: 'E',   label: 'Grupo E', subtitle: 'Alemania · Curazao · Costa de Marfil · Ecuador',           from: 341, to: 420, countries: ['GER', 'CUW', 'CIV', 'ECU'] },
  { id: 'F',   label: 'Grupo F', subtitle: 'Países Bajos · Japón · Suecia · Túnez',                    from: 421, to: 500, countries: ['NED', 'JPN', 'SWE', 'TUN'] },
  { id: 'G',   label: 'Grupo G', subtitle: 'Bélgica · Egipto · Irán · Nueva Zelanda',                  from: 501, to: 580, countries: ['BEL', 'EGY', 'IRN', 'NZL'] },
  { id: 'H',   label: 'Grupo H', subtitle: 'España · Cabo Verde · Arabia Saudita · Uruguay',           from: 581, to: 660, countries: ['ESP', 'CPV', 'KSA', 'URU'] },
  { id: 'I',   label: 'Grupo I', subtitle: 'Francia · Senegal · Irak · Noruega',                       from: 661, to: 740, countries: ['FRA', 'SEN', 'IRQ', 'NOR'] },
  { id: 'J',   label: 'Grupo J', subtitle: 'Argentina · Argelia · Austria · Jordania',                 from: 741, to: 820, countries: ['ARG', 'ALG', 'AUT', 'JOR'] },
  { id: 'K',   label: 'Grupo K', subtitle: 'Portugal · RD del Congo · Uzbekistán · Colombia',          from: 821, to: 900, countries: ['POR', 'COD', 'UZB', 'COL'] },
  { id: 'L',   label: 'Grupo L', subtitle: 'Inglaterra · Croacia · Ghana · Panamá',                    from: 901, to: 980, countries: ['ENG', 'CRO', 'GHA', 'PAN'] },
  { id: 'CC',  label: 'CC',      subtitle: 'Coca-Cola',                              from: 981, to: 994, codePrefix: 'CC' },
];

const HIDDEN_FROM = 995; // CC 995-1000 no longer exist in the design
const HIDDEN_TO = 1000;
function isHiddenStickerIdx(i) {
  const n = i + 1;
  return n >= HIDDEN_FROM && n <= HIDDEN_TO;
}
export function isHiddenSticker(n) {
  return n >= HIDDEN_FROM && n <= HIDDEN_TO;
}

// Slot 20 es la portada FWC00 (no existe FWC20 en el print real). El cambio
// vive sólo acá: stickerParts devuelve num=0 con pad=2 para slot 20, y
// codeToNumber (quickAddParser.js) reconoce "FWC0"/"FWC00" como slot 20.
const FWC_COVER_SLOT = 20;

function stickerParts(n) {
  if (n < 1 || n > TOTAL || isHiddenSticker(n)) return null;
  for (const sec of ALBUM_SECTIONS) {
    if (n < sec.from || n > sec.to) continue;
    const offset = n - sec.from;
    if (sec.codePrefix === 'FWC' && n === FWC_COVER_SLOT) {
      return { prefix: 'FWC', num: 0, pad: 2 };
    }
    if (sec.codePrefix) {
      return { prefix: sec.codePrefix, num: offset + 1 };
    }
    if (sec.countries) {
      const country = Math.floor(offset / 20);
      const idx = (offset % 20) + 1;
      return { prefix: sec.countries[country] || sec.id, num: idx };
    }
    return { prefix: sec.id, num: offset + 1 };
  }
  return null;
}

function formatNum(p) {
  return p.pad ? String(p.num).padStart(p.pad, '0') : String(p.num);
}

/**
 * Translate a 1-indexed sticker number into its full display code, e.g.
 *   1 → "FWC1"     19 → "FWC19"    20 → "FWC00" (portada)
 *   21 → "USA1"    40 → "USA20"     41 → "NED1"
 *   981 → "CC1"    994 → "CC14"
 * Returns the bare number for hidden slots (only ever surfaced for debug).
 */
export function stickerCode(n) {
  const p = stickerParts(n);
  return p ? `${p.prefix}${formatNum(p)}` : String(n);
}

/**
 * Just the in-section number (1..20 inside a country, 1..19 + "00" en FWC).
 * Used inside the grid cell where the surrounding box already carries the
 * country/section label, so the cell only needs the index.
 */
export function stickerLocalNumber(n) {
  const p = stickerParts(n);
  return p ? formatNum(p) : n;
}

/* ── Bulk album cache (localStorage) ─────────────────────────────────── */

const CACHE_KEY = 'album-bulk-cache';

/**
 * Load the cached bulk data from localStorage.
 * Returns { serverTime, albums: { [userId]: { n, d, t } } } or null.
 */
export function loadBulkCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

/**
 * Replace bulk data in localStorage. El backend devuelve siempre la lista
 * canónica completa de coleccionistas alcanzables (que comparten comunidad
 * con el caller), así que cada respuesta sobrescribe el cache. Esto evita
 * arrastrar entradas viejas de gente que dejó de compartir comunidad.
 * entries: array of { u, n, d, t, c } from the bulk endpoint.
 */
export function replaceBulkCache(serverTime, entries) {
  const albums = {};
  for (const e of entries) {
    albums[e.u] = { n: e.n, d: e.d, t: e.t, c: Array.isArray(e.c) ? e.c : [] };
  }
  const next = { serverTime, albums };
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(next));
  } catch { /* localStorage full — fail silently */ }
  return next;
}

/**
 * Get all cached users as an array: [{ id, name, initial, color, owned }].
 * Si se pasa `myCommunityIds` (Set | array | null), se filtran las entradas
 * cuyos communityIds no se solapen con las comunidades actuales del usuario.
 * Esto asegura que si el usuario se va de una comunidad, los coleccionistas
 * que sólo compartían esa comunidad dejan de aparecer en matches/búsquedas.
 */
export function getCachedUsers(cache, myCommunityIds) {
  if (!cache?.albums) return [];
  const filterSet = myCommunityIds instanceof Set
    ? myCommunityIds
    : Array.isArray(myCommunityIds) ? new Set(myCommunityIds) : null;
  const out = [];
  for (const [uid, entry] of Object.entries(cache.albums)) {
    const id = Number(uid);
    const communityIds = Array.isArray(entry.c) ? entry.c : [];
    if (filterSet) {
      if (communityIds.length === 0) continue;
      let shared = false;
      for (const cid of communityIds) {
        if (filterSet.has(cid)) { shared = true; break; }
      }
      if (!shared) continue;
    }
    out.push({
      id,
      name: entry.n,
      initial: userInitial(entry.n),
      color: userColor(id),
      owned: ownedCountFromBase64(entry.d),
      communityIds,
    });
  }
  return out;
}

/* ── Precomputed trade index (for 10K+ users) ───────────────────────── */

// Tamaño del bitmap: 1000 stickers → ceil(1000/32) = 32 uint32s
const BITMAP_LEN = Math.ceil(TOTAL / 32);

export function buildBitmapFromCounts(counts, predicate) {
  const bm = new Uint32Array(BITMAP_LEN);
  if (!counts) return bm;
  for (let i = 0; i < TOTAL; i++) {
    if (isHiddenStickerIdx(i)) continue;
    if (predicate(counts[i])) bm[i >> 5] |= 1 << (i & 31);
  }
  return bm;
}

function buildBitmap(base64, predicate) {
  const bm = new Uint32Array(BITMAP_LEN);
  if (!base64) return bm;
  const bytes = decodeAlbumBytes(base64);
  for (let i = 0; i < TOTAL; i++) {
    if (isHiddenStickerIdx(i)) continue;
    if (predicate(bytes[i])) bm[i >> 5] |= 1 << (i & 31);
  }
  return bm;
}

function popcount32(x) {
  x = x - ((x >> 1) & 0x55555555);
  x = (x & 0x33333333) + ((x >> 2) & 0x33333333);
  return (((x + (x >> 4)) & 0x0F0F0F0F) * 0x01010101) >> 24;
}

function bitmapAndCount(a, b) {
  let count = 0;
  for (let i = 0; i < BITMAP_LEN; i++) count += popcount32(a[i] & b[i]);
  return count;
}

/**
 * Devuelve los slots (1-indexed) donde ambos bitmaps tienen el bit prendido,
 * en orden ascendente. Útil para materializar la lista concreta cuando ya
 * sabemos que la cuenta es la que queremos.
 */
export function bitmapAndToList(a, b) {
  const out = [];
  for (let i = 0; i < TOTAL; i++) {
    if (isHiddenStickerIdx(i)) continue;
    const w = i >> 5;
    const bit = 1 << (i & 31);
    if ((a[w] & b[w] & bit) !== 0) out.push(i + 1);
  }
  return out;
}

/**
 * Construye un índice precomputado de bitmaps para matching rápido.
 * Se calcula UNA vez cuando cambia bulkCache.
 *
 * Retorna: Map<userId, { spare: Uint32Array, needs: Uint32Array }>
 *   spare = bitmap de figuritas con count >= 2 (disponibles para dar)
 *   needs = bitmap de figuritas con count == 0 (las necesita)
 */
export function buildTradeIndex(cache) {
  if (!cache?.albums) return new Map();
  const index = new Map();
  for (const [uid, entry] of Object.entries(cache.albums)) {
    index.set(Number(uid), {
      spare: buildBitmap(entry.d, c => c >= 2),
      needs: buildBitmap(entry.d, c => c === 0),
    });
  }
  return index;
}

/**
 * Encuentra el mejor match para trading usando bitmaps precomputados.
 * Solo cuenta matches recíprocos: el otro tiene que poder darme algo Y yo
 * tengo que poder darle algo, si no, no hay intercambio que armar.
 *
 * Score = min(theyCanGive, iCanGive) con desempate por suma — coherente
 * con findTopMatches (ver comentario allí sobre por qué no usamos la suma
 * como término principal).
 */
export function findBestMatch(mySpare, myNeeds, allUsers, tradeIndex) {
  let best = null;
  let bestScore = 0;
  let bestSum = 0;
  for (const user of allUsers) {
    const their = tradeIndex.get(user.id);
    if (!their) continue;
    const theyCanGive = bitmapAndCount(their.spare, myNeeds);
    const iCanGive = bitmapAndCount(mySpare, their.needs);
    if (theyCanGive === 0 || iCanGive === 0) continue;
    const score = Math.min(theyCanGive, iCanGive);
    const sum = theyCanGive + iCanGive;
    if (score > bestScore || (score === bestScore && sum > bestSum)) {
      bestScore = score;
      bestSum = sum;
      best = { user, theyCanGive, iCanGive };
    }
  }
  return best;
}

/**
 * Returns the top-N users sorted by trade score (descending). Solo recíprocos:
 * descarta a cualquiera donde una de las dos partes no pueda dar nada.
 *
 * Score = min(theyCanGive, iCanGive) — esa es la cantidad de figuritas
 * realmente intercambiables, que es lo que la UI muestra como "te dan N /
 * les das N". Antes el score sumaba también theyCanGive+iCanGive como
 * desempate, lo que producía un orden contra-intuitivo: alguien con
 * theyCanGive=100/iCanGive=7 (UI muestra 7/7) terminaba arriba de alguien
 * con 25/25 porque el "potencial total" pesaba en el sort.
 *
 * Si dos users empatan en min, desempatamos por la suma para no perder el
 * dato de volumen total.
 */
export function findTopMatches(mySpare, myNeeds, allUsers, tradeIndex, limit = 5) {
  const scored = [];
  for (const user of allUsers) {
    const their = tradeIndex.get(user.id);
    if (!their) continue;
    const theyCanGive = bitmapAndCount(their.spare, myNeeds);
    const iCanGive = bitmapAndCount(mySpare, their.needs);
    if (theyCanGive === 0 || iCanGive === 0) continue;
    const score = Math.min(theyCanGive, iCanGive);
    scored.push({ user, theyCanGive, iCanGive, score });
  }
  scored.sort((a, b) =>
    (b.score - a.score) ||
    ((b.theyCanGive + b.iCanGive) - (a.theyCanGive + a.iCanGive))
  );
  return scored.slice(0, limit);
}

/**
 * Encuentra matches para una selección de figuritas.
 * selected: Set de sticker numbers, myCounts: Uint8Array
 */
export function findSelectionMatches(selected, myCounts, allUsers, tradeIndex) {
  if (selected.size === 0 || !myCounts) return { matches: [], wantCount: 0, offerCount: 0 };

  const iWant = [];
  const iOffer = [];
  for (const n of selected) {
    if (myCounts[n - 1] === 0) iWant.push(n);
    else if (myCounts[n - 1] >= 2) iOffer.push(n);
  }

  const scored = allUsers.map(user => {
    const their = tradeIndex.get(user.id);
    if (!their) return { user, theyCanGive: 0, theyNeed: 0, score: 0 };
    let theyCanGive = 0;
    for (const n of iWant) {
      const i = n - 1;
      if (their.spare[i >> 5] & (1 << (i & 31))) theyCanGive++;
    }
    let theyNeed = 0;
    for (const n of iOffer) {
      const i = n - 1;
      if (their.needs[i >> 5] & (1 << (i & 31))) theyNeed++;
    }
    return { user, theyCanGive, theyNeed, score: theyCanGive * 2 + theyNeed };
  });

  return {
    matches: scored.filter(m => m.score > 0).sort((a, b) => b.score - a.score).slice(0, 5),
    wantCount: iWant.length,
    offerCount: iOffer.length,
  };
}
